import { useCallback, useEffect, useRef, useState } from "react";
import { Hand, Microphone, Keyboard, Lifebuoy } from "@phosphor-icons/react";
import { kioskApi, errText, isKioskDevice, isTableQr, runFlow, isServiceError } from "@/lib/kioskApi";
import { TableOnlinePay } from "@/components/kiosk/TableOnlinePay";
import { OptionPicker } from "@/components/kiosk/KioskMenuSheet";
import { AiBrowseMenu } from "@/components/kiosk/AiBrowseMenu";
import { useSpeech } from "@/components/kiosk/useSpeech";
import { useTurnEngine } from "@/components/kiosk/useTurnEngine";
import { KioskOrderPanel } from "@/components/kiosk/KioskOrderPanel";
import { useKioskHelp, HelpButton, HelpBanner, NewOrderButton } from "@/components/kiosk/KioskHelp";
import { KioskOpening } from "@/components/kiosk/KioskOpening";
import { FlowPanel } from "@/components/kiosk/FlowPanel";
import { KioskCard, KioskDone } from "@/components/kiosk/KioskCheckout";
import { useKioskHold } from "@/components/kiosk/KioskExit";
import { StaffTestBanner } from "@/components/kiosk/StaffTestBanner";
import "@/components/kiosk/kiosk.css";

const TROUBLE = "Sorry, I had trouble with that — your order is safe. Please try again.";
const classicUrl = (b) => `/order?mode=kiosk&venue=${b?.venue_id || ""}`;
const protectedNow = (s) => !!s && (s.busy || s.credit?.status === "pending" || ["queued", "processing", "checking"].includes(s.card?.state) || ["confirming", "online"].includes(s.stage));
const FOOD = ["food", "food_correct", "more", "final_correct"];
const statusFor = (step, body) => (body.text && FOOD.concat("food_confirm", "final_review").includes(step) ? "Checking your order…"
  : step === "food_confirm" && body.button === "yes" ? "Adding to cart…" : step === "final_review" && body.button === "yes" ? "Placing your order…" : "Nauti is thinking…");

export default function Kiosk() {
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState("");
  const [session, setSession] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuCat, setMenuCat] = useState(null);
  const [idleAsk, setIdleAsk] = useState(false);
  const [muted, setMuted] = useState(localStorage.getItem("mm_kiosk_muted") === "1");
  const [opt, setOpt] = useState(null);
  const qr = isTableQr();
  const lastBody = useRef(null);
  const last = useRef(Date.now());
  const sref = useRef(null);
  sref.current = session;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const touch = useCallback(() => { last.current = Date.now(); setIdleAsk(false); }, []);
  const aiOn = boot?.budget?.ai_available !== false;
  const volume = Number(localStorage.getItem("mm_kiosk_volume") || 1);
  const engine = useTurnEngine({ onUtterance: () => {}, tapMode: true });
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const speech = useSpeech({ engine, mutedRef, volume });
  const { run, cancel: stopVoice, forget, newSession } = speech;
  // Every spoken line is the owner's saved wording (server-rendered); this file only decides WHEN it plays.
  // Diagnostics: how each clip ended (completed / interrupted / cancelled / muted) — keys and outcomes only.
  const report = (sid, kind, seq) => (key, outcome) => { if (key) kioskApi.post(`/sessions/${sid}/speech-log`, { kind, key, outcome, seq }).catch(() => {}); };
  const flowLine = useCallback((s) => ({ relevant: () => sref.current?.id === s.id && sref.current?.flow?.seq === s.flow.seq, report: report(s.id, "prompt", s.flow.seq),
    load: () => kioskApi.get(`/sessions/${s.id}/flow/audio`, { params: { seq: s.flow.seq }, timeout: 20000 }).then((r) => r.data) }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const phraseLine = (key, sid) => ({ key, once: key, local: key === "inactivity", relevant: () => sref.current?.id === sid, report: report(sid, "phrase"),
    load: () => kioskApi.get(`/sessions/${sid}/phrase/${key}`).then((r) => r.data) });
  const canSpeak = useCallback((s) => !!s?.flow?.seq && !mutedRef.current && aiOn && !s.flow.touch_only && s.flow.prompt?.speak !== false, [aiOn]);
  // Voice for the CURRENT question: text is already on screen, audio follows and is skipped if the question moved on.
  const speakPrompt = useCallback((s) => { if (canSpeak(s)) run([flowLine(s)]); }, [canSpeak, run, flowLine]);
  const sayKey = useCallback((key, sid, opts) => {
    if (!sid || sref.current?.flow?.touch_only) return Promise.resolve();
    return run([phraseLine(key, sid)], opts);
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isKioskDevice()) { setBootErr("This device isn't set up as a kiosk. A manager can switch it in Device mode (staff login → Device mode)."); return; }
    kioskApi.get("/bootstrap").then((r) => setBoot(r.data)).catch((e) => setBootErr(errText(e)));
    kioskApi.get("/sessions/current").then((r) => r.data.session && setSession(r.data.session)).catch(() => {});
  }, []);
  useEffect(() => { // Owner's AI ON/OFF switch: off → this device uses the original touchscreen kiosk.
    if (!qr && boot && !boot.kiosk_enabled && !protectedNow(session) && !session?.order) window.location.replace(classicUrl(boot));
  }, [boot, session]);
  useEffect(() => {
    if (session) return undefined;
    const t = setInterval(() => kioskApi.get("/bootstrap").then((r) => setBoot(r.data)).catch(() => {}), 20000);
    return () => clearInterval(t);
  }, [session]);

  const start = async (touchOnly = false) => {
    touch(); setNotice("");
    try {
      const r = await kioskApi.post("/sessions", { touch_only: touchOnly === true });
      const s = r.data.session;
      newSession();
      setSession(s);
      // Opening sequence (after the customer's tap, so the browser allows audio): catchphrase → subtitle →
      // TAP TO ORDER welcome, each only if the owner enabled it, once per session, one at a time.
      if (canSpeak(s)) run([phraseLine("opening_headline", s.id), phraseLine("opening_subtitle", s.id), flowLine(s)]);
    } catch (e) { if (e?.response?.status === 403) window.location.replace(classicUrl(boot)); else setNotice(isServiceError(e) ? TROUBLE : errText(e)); }
  };

  const answer = useCallback(async (body) => {
    const s = sref.current;
    if (!s?.flow || busy) return;
    touch(); stopVoice(); setNotice("");
    lastBody.current = body;
    setBusy(statusFor(s.flow.step, body));
    try {
      const r = await runFlow(s.id, { seq: s.flow.seq, origin_url: window.location.origin, ...body });
      if (sref.current?.id !== s.id) return; // a new order started meanwhile — ignore the old answer
      setSession(r.session);
      lastBody.current = null;
      if (r.session.flow?.seq !== s.flow.seq && !["card", "done"].includes(r.session.stage)) speakPrompt(r.session);
    } catch (e) {
      if (sref.current?.id !== s.id) return;
      const st = e?.response?.status;
      if (st === 409) { kioskApi.get(`/sessions/${s.id}`).then((x) => setSession(x.data.session)).catch(() => {}); setNotice(errText(e)); lastBody.current = null; }
      else if (st === 403) window.location.replace(classicUrl(boot));
      else setNotice(isServiceError(e) ? TROUBLE : errText(e));
    } finally { if (sref.current?.id === s.id) setBusy(""); }
  }, [busy, touch, stopVoice, speakPrompt, boot]);

  const reset = useCallback(async (why) => {
    const s = sref.current;
    newSession(); engineRef.current.release();
    if (s) {
      try { await kioskApi.post(`/sessions/${s.id}/${s.stage === "done" ? "finish" : "abandon"}`, s.stage === "done" ? undefined : { reason: why }); }
      catch (e) { if (e?.response?.status === 409) { touch(); setNotice(`Can't start again yet — ${errText(e).toLowerCase()}. Ask staff for help if needed.`); return; } }
    }
    setSession(null); setNotice(""); setMenuOpen(false); setMenuCat(null); setIdleAsk(false); setBusy("");
  }, [touch, newSession]);
  const help = useKioskHelp(session?.id || "device", stopVoice);
  useEffect(() => {
    if (help.status !== "acknowledged" || mutedRef.current) return;
    if (sref.current) { forget("help_ack"); sayKey("help_ack", sref.current.id); } // no order yet: shown on screen only (no unsaved wording is ever spoken)
  }, [help.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardState = session?.card?.state;
  useEffect(() => {
    if (["declined", "cancelled", "expired", "timeout", "error"].includes(cardState)) { forget("card_failed"); sayKey("card_failed", sref.current?.id); }
  }, [cardState, sayKey, forget]);
  useEffect(() => {
    if (session?.stage !== "done") return;
    const pm = session.order?.payment_method;
    sayKey(pm === "pay_at_counter" ? (qr ? "qr_cash_done" : "cash_done") : pm === "credit" ? "credit_done" : "card_done", session.id);
  }, [session?.stage]); // eslint-disable-line react-hooks/exhaustive-deps
  const finishDone = useCallback(async () => {
    const s = sref.current;
    if (s) await Promise.race([sayKey("goodbye", s.id, { queue: true }), new Promise((r) => { setTimeout(r, 15000); })]); // after "thank you", once
    reset("finished");
  }, [reset, sayKey]);

  // 60 s: "Are you still there?"  90 s: abandon the draft (never while protected work is in progress).
  useEffect(() => {
    const t = setInterval(() => {
      const s = sref.current;
      if (!s || s.stage === "done") return;
      if (protectedNow(s) || busy || engineRef.current.state === "speaking") { last.current = Date.now(); return; }
      const idle = (Date.now() - last.current) / 1000;
      if (idle >= (boot?.idle?.reset_seconds || 90)) reset("inactive");
      else if (idle >= (boot?.idle?.warn_seconds || 60) && !idleAsk) { setIdleAsk(true); forget("inactivity"); sayKey("inactivity", s.id); }
    }, 1000);
    return () => clearInterval(t);
  }, [boot, busy, idleAsk, reset, sayKey, forget]);
  useEffect(() => {
    if (session?.credit?.status !== "pending") return undefined;
    const t = setInterval(() => kioskApi.get(`/sessions/${session.id}`).then((r) => setSession(r.data.session)).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [session?.credit?.status, session?.id]);

  const act = async (actions) => {
    touch(); setNotice("");
    try { const r = await kioskApi.post(`/sessions/${session.id}/actions`, { actions }); setSession(r.data.session); if (r.data.notices?.length) setNotice(r.data.notices.join(" ")); }
    catch (e) { setNotice(errText(e)); }
  };
  // Browse Menu → "Continue to checkout" goes straight to the AI payment step (Back still returns to the order).
  const browseCheckout = () => {
    const f = sref.current?.flow;
    setMenuOpen(false);
    if (sref.current?.cart.length && ["food", "food_correct", "more", "final_correct"].includes(f?.step)) answer({ button: "checkout" });
  };
  // Options / Modifiers: from a tap, or when the customer asked by voice (server sets flow.show_options).
  const openOptions = useCallback((target, index, s = sref.current) => {
    const ln = target === "cart" ? s?.cart?.[index] : s?.flow?.proposal?.lines?.[index];
    const item = boot?.menu?.find((m) => m.id === ln?.menu_item_id);
    if (item?.groups?.length) setOpt({ target, index, item, qty: ln.qty, initial: ln.modifiers.map((m) => m.modifier_option_id) });
  }, [boot]);
  const showNonce = session?.flow?.show_options?.nonce;
  useEffect(() => {
    const so = sref.current?.flow?.show_options;
    if (!so) return;
    if (so.target === "menu") setMenuOpen(true); else openOptions(so.target, so.index);
  }, [showNonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveOptions = async (ids, one = false) => {
    const o = opt; setOpt(null);
    try {
      const before = sref.current?.flow?.seq;
      const r = await kioskApi.post(`/sessions/${session.id}/line-options`, { target: o.target, index: o.index, option_ids: ids, one });
      setSession(r.data.session);
      if (r.data.session.flow?.seq !== before) speakPrompt(r.data.session);
    } catch (e) { setNotice(errText(e)); }
  };
  const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem("mm_kiosk_muted", m ? "1" : "0"); if (m) stopVoice(); };
  const voice = { muted, toggleMute, speaking: engine.state === "speaking", stop: stopVoice, repeat: () => { if (muted) toggleMute(); mutedRef.current = false; speakPrompt(sref.current); } };

  // Staff AI test ends (Exit, PIN menu or time up) → back to customer-safe mode; any test order is discarded.
  const endTest = async () => {
    stopVoice();
    let ai = false;
    try { ai = !!(await kioskApi.post("/staff-test/end")).data.customer_ai_enabled; } catch { ai = false; }
    window.location.replace(ai ? "/kiosk" : classicUrl(boot));
  };
  const { bind: logo, overlay: exitOverlay } = useKioskHold({ busy: protectedNow(session), staffTest: boot?.staff_test, onEndTest: endTest });
  const fallbackLogo = <div className="absolute top-6 left-8 text-white text-3xl font-bold font-heading" {...logo} data-testid="kiosk-logo">Order here</div>;
  if (bootErr) return <div className="kiosk-root flex flex-col items-center justify-center gap-6 p-10 text-center text-2xl text-white" data-testid="kiosk-error">{isKioskDevice() && fallbackLogo}{bootErr}{!isKioskDevice() && <a href="/login" className="kiosk-pill bg-white/10 text-white text-base" data-testid="kiosk-error-login">Staff login</a>}{exitOverlay}</div>;
  if (!boot) return <div className="kiosk-root" data-testid="kiosk-loading">{fallbackLogo}{exitOverlay}</div>;
  return (
    <div className={`kiosk-root ${qr ? "kiosk-qr" : ""}`} data-testid="kiosk-page" data-channel={qr ? "table_qr" : "counter"} onPointerDown={touch}>
      <div className="kiosk-grain" />
      {boot.staff_test && <StaffTestBanner staffTest={boot.staff_test} onEnd={endTest} />}
      <header className="relative z-10 flex flex-wrap items-center gap-3 px-4 sm:px-8 pt-4 sm:pt-6 [&_button]:whitespace-nowrap">
        <div {...logo} data-testid="kiosk-logo" className="min-w-0 mr-auto">
          <div className="text-white/60 text-xs tracking-[0.3em] uppercase flex items-center gap-2">Order here
            {boot.test_mode && !boot.staff_test && <span className="rounded-full bg-[#fff2cc] text-[#5c4300] text-[10px] tracking-normal font-bold px-2 py-0.5 whitespace-nowrap" data-testid="kiosk-test-mode-badge">TEST MODE</span>}</div>
          <div className="text-white text-2xl sm:text-3xl font-bold font-heading truncate max-w-[60vw]">{boot.venue}</div>
        </div>
        {boot.table && <span className="kiosk-pill bg-[#ffd166] text-[#0f2a3d] font-bold whitespace-nowrap" data-testid="kiosk-table-badge">Table {boot.table.number}</span>}
        {session && <div className="flex items-center gap-2 sm:gap-3"><NewOrderButton hasItems={session.cart.length > 0 || !!session.order_type || !!session.customer_name} onConfirm={() => reset("new_order")} /><HelpButton help={help} /></div>}
      </header>
      <HelpBanner help={help} />
      {!session ? <KioskOpening boot={boot} onStart={start} aiOn={aiOn} notice={notice} muted={muted} onToggleMute={toggleMute} classicUrl={qr || boot.staff_test ? null : classicUrl(boot)} qr={qr} helpButton={<HelpButton help={help} />} /> : (
        <main className={`relative z-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-8 px-4 sm:px-8 pb-8 pt-4 ${boot.staff_test ? (help.message ? "lg:h-[calc(100vh-212px)]" : "lg:h-[calc(100vh-140px)]") : help.message ? "lg:h-[calc(100vh-168px)]" : "lg:h-[calc(100vh-96px)]"}`}>
          <section className="flex flex-col min-h-0 lg:overflow-y-auto pr-1 [&>*]:shrink-0">
            {session.flow ? <FlowPanel session={session} boot={boot} busy={!!busy} status={busy} onAnswer={answer} voice={voice} onBrowse={() => setMenuOpen(true)} onActivity={touch} onOptions={(i) => openOptions("proposal", i)} />
              : <div className="text-white text-2xl" data-testid="kiosk-legacy-session">This order was started before an update. <button className="underline" onClick={() => reset("legacy")}>Start a new order</button></div>}
            {notice && <div className="mt-4 px-4 py-2 rounded-2xl bg-[#fff2cc] text-[#5c4300] self-start" data-testid="kiosk-notice">{notice}</div>}
            {notice === TROUBLE && (
              <div className="flex flex-wrap gap-3 mt-3" data-testid="kiosk-trouble-actions">
                {lastBody.current && <button className="kiosk-pill bg-white text-[#0f2a3d]" data-testid="kiosk-trouble-retry" onClick={() => answer(lastBody.current)}><Microphone size={18} /> Retry</button>}
                <button className="kiosk-pill bg-white/10 text-white" data-testid="kiosk-trouble-menu" onClick={() => { setNotice(""); setMenuOpen(true); }}><Keyboard size={18} /> Browse menu instead</button>
                <button className="kiosk-pill bg-white/10 text-white" data-testid="kiosk-trouble-help" onClick={help.request}><Lifebuoy size={18} /> Get help</button>
              </div>
            )}
          </section>
          <KioskOrderPanel session={session} onAction={(a) => act([a])} onUseCredit={(amt) => act([amt ? { type: "use_credit", amount: amt } : { type: "decline_credit" }])} locked={!!session.order} onOptions={(i) => openOptions("cart", i)} menu={boot.menu} />
        </main>
      )}
      {menuOpen && session && !session.order && <AiBrowseMenu boot={boot} session={session} qr={qr} activeCat={menuCat} setActiveCat={setMenuCat} onAction={act}
        onClose={() => { touch(); setMenuOpen(false); }} onCheckout={browseCheckout} help={help} notice={notice} />}
      {opt && <OptionPicker item={opt.item} initial={opt.initial} qty={opt.qty} actionLabel="Save options" onCancel={() => setOpt(null)} onAdd={saveOptions} />}
      {session?.stage === "online" && <TableOnlinePay session={session} onUpdate={setSession} onHelp={help.request} cashOk={boot.tenders.cash} />}
      {session?.stage === "card" && <KioskCard session={session} onUpdate={setSession} simulated={boot.card_simulated} cashOk={boot.tenders.cash} />}
      {session?.stage === "done" && <KioskDone session={session} seconds={boot.mic?.done_display_seconds || 20} onDone={finishDone} />}
      {idleAsk && session && <div className="fixed inset-x-0 bottom-10 z-50 flex justify-center" data-testid="kiosk-idle-warning"><button className="kiosk-big bg-white !text-[#0f2a3d] shadow-2xl" onClick={touch}><Hand size={22} /> Are you still there? Tap to keep ordering</button></div>}
      {exitOverlay}
    </div>
  );
}
